<script lang="ts" setup>
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useConvertProjectToProduct } from "@/queries/projects";
import FormAlert from "@/components/FormAlert.vue";
import type { Project } from "@/types";

const props = defineProps<{
  show: boolean;
  project: Project | null;
}>();

const emit = defineEmits<{
  "update:show": [value: boolean];
  converted: [productId: string];
}>();

const router = useRouter();
const { convertProjectToProduct, convertPending, convertError, convertReset } = useConvertProjectToProduct();

const productName = ref("");
const productDescription = ref("");
const clientValidationError = ref("");

// Watch for modal opening to initialize form
watch(
  () => props.show,
  (newShow) => {
    if (newShow && props.project) {
      // Pre-populate with project name as starting point
      productName.value = props.project.name;
      productDescription.value = "";
      clientValidationError.value = "";
      convertReset();
    }
  }
);

function closeModal() {
  emit("update:show", false);
}

function handleConvert() {
  if (!props.project) return;

  clientValidationError.value = "";

  if (!productName.value.trim()) {
    clientValidationError.value = "Product name is required";
    return;
  }

  if (!productDescription.value.trim()) {
    clientValidationError.value = "Product description is required";
    return;
  }

  convertProjectToProduct({
    projectId: props.project.id,
    name: productName.value.trim(),
    description: productDescription.value.trim(),
  }, {
    onSuccess: (result) => {
      emit("converted", result.id);
      closeModal();
      router.push(`/publisher/${result.id}`);
    },
  });
}
</script>

<template>
  <div
    class="modal fade"
    :class="{ show: show }"
    style="display: block"
    v-if="show"
    @click.self="closeModal"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">Convert to Product</h2>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <FormAlert :error="clientValidationError || convertError" @dismiss="() => { clientValidationError = ''; convertReset(); }" />

          <div class="mb-3">
            <p class="text-muted">
              Convert "{{ project?.name }}" into a publishable product. This
              will:
            </p>
            <ul class="text-muted small">
              <li>Create a new product listing</li>
              <li>
                Convert this project to a "Master Project" for the product
              </li>
              <li>Enable publishing and subscriber management features</li>
              <li>Allow you to configure pricing and distribution settings</li>
            </ul>
          </div>

          <div class="mb-3">
            <label for="productName" class="form-label"
              >Product Name <span class="text-danger">*</span></label
            >
            <input
              type="text"
              class="form-control"
              id="productName"
              v-model="productName"
              placeholder="Enter product name"
              :disabled="convertPending"
            />
          </div>

          <div class="mb-3">
            <label for="productDescription" class="form-label"
              >Product Description <span class="text-danger">*</span></label
            >
            <textarea
              class="form-control"
              id="productDescription"
              v-model="productDescription"
              rows="3"
              placeholder="Describe what this product does and who it's for"
              :disabled="convertPending"
            ></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="closeModal"
            :disabled="convertPending"
          >
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            @click="handleConvert"
            :disabled="convertPending"
          >
            <span
              v-if="convertPending"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            {{ convertPending ? "Converting..." : "Convert to Product" }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" v-if="show" @click="closeModal"></div>
</template>
